import { Component, DestroyRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService, DynamicEntityDetail } from './dynamic-api.service';
import { DynamicErdComponent } from './dynamic-erd.component';
import { DynamicFieldEditorComponent } from './dynamic-field-editor.component';
import { DynamicIndexEditorComponent } from './dynamic-index-editor.component';
import { DynamicRelationshipEditorComponent } from './dynamic-relationship-editor.component';
import { DynamicVersionHistoryComponent } from './dynamic-version-history.component';
import { AiAuthoringWizardComponent } from './ai-authoring/ai-authoring-wizard.component';
import { hasAiGeneratePermission } from './ai-authoring/ai-authoring.util';

/** Designer tabs — sibling tab components slot in by chunk (see tab body comments). */
type DesignerTab = 'fields' | 'relationships' | 'erd' | 'indexes' | 'versions';

interface MetadataForm {
  displayName: string;
  description: string;
  iconKey: string;
}

@Component({
  standalone: true,
  selector: 'ain-dynamic-entity-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    DynamicFieldEditorComponent,
    DynamicRelationshipEditorComponent,
    DynamicIndexEditorComponent,
    DynamicErdComponent,
    DynamicVersionHistoryComponent,
    AiAuthoringWizardComponent,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    TagModule,
    ConfirmDialogModule,
    FormsModule,
    RouterLink
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <ain-page
      [eyebrow]="t('dynamic.designer.title')"
      [title]="entity()?.displayName ?? t('dynamic.designer.title')"
      [description]="t('dynamic.designer.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.designer.info.title'"
        [descriptionKey]="'dynamic.designer.info.description'"
        [bulletKeys]="[
          'dynamic.designer.info.bullets.scope',
          'dynamic.designer.info.bullets.versioning',
          'dynamic.designer.info.bullets.relationships'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (entity(); as ent) {
        <header class="designer-header card">
          <div class="toolbar">
            <a class="back-link" routerLink="/dynamic/entities">{{ t('common.back') }}</a>
            <div class="actions">
              @if (canGenerateWithAi()) {
                <button
                  pButton
                  type="button"
                  severity="help"
                  icon="pi pi-sparkles"
                  [label]="t('dynamic.aiAuthoring.generateWithAi')"
                  (click)="openAiAuthoringWizard()"></button>
              }
              @if (canUpdateEntity()) {
                <button pButton type="button" severity="secondary" [label]="t('dynamic.designer.editMetadata')" (click)="openMetadataDialog()"></button>
              }
              @if (!ent.isActive && canActivateEntity()) {
                <button pButton type="button" [label]="t('dynamic.designer.activate')" (click)="confirmActivate()"></button>
              }
              @if (ent.isActive && canArchiveEntity()) {
                <button pButton type="button" severity="danger" [label]="t('dynamic.designer.archive')" (click)="confirmArchive()"></button>
              }
              @if (canDeleteEntity()) {
                <button pButton type="button" severity="danger" [label]="t('dynamic.entities.delete')" (click)="confirmDelete()"></button>
              }
              <button pButton type="button" severity="help" [label]="t('dynamic.migration.generate')" (click)="generateMigration()"></button>
              <a class="records-link" [routerLink]="['/dynamic/entities', ent.id, 'records']">{{ t('dynamic.designer.viewRecords') }}</a>
            </div>
          </div>

          <div class="summary">
            <span>{{ t('dynamic.designer.logicalName') }}: <strong>{{ ent.name }}</strong></span>
            <span>{{ t('dynamic.designer.physicalTable') }}: <strong>{{ ent.tableName }}</strong></span>
            <span>{{ t('dynamic.designer.currentVersion') }}: <strong>{{ ent.currentVersion }}</strong></span>
            <span>
              {{ t('columns.status') }}:
              <p-tag
                [value]="ent.isActive ? t('dynamic.designer.status.active') : t('dynamic.designer.status.archived')"
                [severity]="ent.isActive ? 'success' : 'secondary'" />
            </span>
          </div>
        </header>

        <div class="tabs">
          <button type="button" [class.active]="activeTab() === 'fields'" (click)="activeTab.set('fields')">{{ t('dynamic.designer.tab.fields') }}</button>
          <button type="button" [class.active]="activeTab() === 'relationships'" (click)="activeTab.set('relationships')">{{ t('dynamic.designer.tab.relationships') }}</button>
          <button type="button" [class.active]="activeTab() === 'erd'" (click)="activeTab.set('erd')">{{ t('dynamic.designer.tab.erd') }}</button>
          <button type="button" [class.active]="activeTab() === 'indexes'" (click)="activeTab.set('indexes')">{{ t('dynamic.designer.tab.indexes') }}</button>
          <button type="button" [class.active]="activeTab() === 'versions'" (click)="activeTab.set('versions')">{{ t('dynamic.designer.tab.versions') }}</button>
        </div>

        @if (activeTab() === 'fields') {
          <section class="card">
            <ain-dynamic-field-editor [entity]="ent" (refresh)="loadEntity(false)" />
          </section>
        }

        @if (activeTab() === 'relationships') {
          <section class="card">
            <ain-dynamic-relationship-editor [entity]="ent" (refresh)="loadEntity(false)" />
          </section>
        }

        @if (activeTab() === 'erd') {
          <section class="card">
            <ain-dynamic-erd />
          </section>
        }

        @if (activeTab() === 'indexes') {
          <section class="card">
            <ain-dynamic-index-editor [entity]="ent" (refresh)="loadEntity(false)" />
          </section>
        }

        @if (activeTab() === 'versions') {
          <section class="card">
            <ain-dynamic-version-history [entity]="ent" />
          </section>
        }
      }

      <p-dialog
        [header]="t('dynamic.designer.editMetadata')"
        [(visible)]="metadataDialogVisible"
        [modal]="true"
        [style]="{ width: 'min(520px, 92vw)' }">
        <div class="dialog-form">
          <ain-form-field [label]="t('columns.displayName')">
            <input pInputText [(ngModel)]="metadataForm.displayName" />
          </ain-form-field>
          <ain-form-field [label]="t('dynamic.entities.descriptionField')">
            <textarea pTextarea rows="3" [(ngModel)]="metadataForm.description"></textarea>
          </ain-form-field>
          <ain-form-field [label]="t('dynamic.designer.iconKey')">
            <input pInputText [(ngModel)]="metadataForm.iconKey" />
          </ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="metadataDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="savingMetadata()" (click)="saveMetadata()"></button>
        </ng-template>
      </p-dialog>

      <p-confirmDialog />

      <ain-ai-authoring-wizard
        #entityAiWizard
        mode="entity"
        [(visible)]="aiWizardVisible" />
    </ain-page>
  `,
  styles: [`
    .designer-header { display: grid; gap: 12px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; margin-bottom: 12px; }
    .toolbar { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; align-items: center; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link, .records-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; color: #374151; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .tabs button { border: 1px solid #d1d5db; background: #fff; border-radius: 999px; padding: 8px 14px; cursor: pointer; }
    .tabs button.active { background: #0f766e; border-color: #0f766e; color: #fff; }
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
    .dialog-form { display: grid; gap: 12px; }
  `]
})
export class DynamicEntityDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);

  readonly entity = signal<DynamicEntityDetail | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly activeTab = signal<DesignerTab>('fields');
  readonly savingMetadata = signal(false);

  metadataDialogVisible = false;
  aiWizardVisible = false;
  metadataForm: MetadataForm = { displayName: '', description: '', iconKey: '' };

  private entityId = '';

  readonly entityAiWizard = viewChild<AiAuthoringWizardComponent>('entityAiWizard');

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.entityId = params.get('id') ?? '';
      if (this.entityId) {
        this.loadEntity();
      }
    });
  }

  loadEntity(showShellLoading = true): void {
    if (!this.entityId) {
      return;
    }

    if (showShellLoading) {
      this.loading.set(true);
      this.error.set(null);
    }

    this.api.getEntity(this.entityId).pipe(
      finalize(() => {
        if (showShellLoading) {
          this.loading.set(false);
        }
      })
    ).subscribe({
      next: ent => this.entity.set(ent),
      error: err => {
        this.entity.set(null);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  openMetadataDialog(): void {
    const ent = this.entity();
    if (!ent) {
      return;
    }

    this.metadataForm = {
      displayName: ent.displayName,
      description: ent.description ?? '',
      iconKey: ''
    };
    this.metadataDialogVisible = true;
  }

  saveMetadata(): void {
    const ent = this.entity();
    const displayName = this.metadataForm.displayName.trim();
    if (!ent || !displayName) {
      return;
    }

    this.savingMetadata.set(true);
    this.api.updateEntity(ent.id, {
      displayName,
      description: this.metadataForm.description.trim() || undefined,
      iconKey: this.metadataForm.iconKey.trim() || undefined
    }).pipe(
      finalize(() => this.savingMetadata.set(false))
    ).subscribe({
      next: updated => {
        this.entity.set({ ...ent, displayName: updated.displayName, description: this.metadataForm.description.trim() || ent.description });
        this.metadataDialogVisible = false;
        this.messages.add({ severity: 'success', summary: this.t('dynamic.designer.metadataSaved') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  confirmActivate(): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.entities.confirm.activate'),
      accept: () => this.activateEntity()
    });
  }

  confirmArchive(): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.entities.confirm.archive'),
      accept: () => this.archiveEntity()
    });
  }

  confirmDelete(): void {
    this.confirmation.confirm({
      header: this.t('dynamic.entities.delete.confirmTitle'),
      message: this.t('dynamic.entities.delete.confirmMessage'),
      accept: () => this.deleteEntity()
    });
  }

  generateMigration(): void {
    const ent = this.entity();
    if (!ent) {
      return;
    }

    this.api.generateMigration(ent.id, {
      changeDescription: this.t('dynamic.migration.generate'),
      isDestructive: false
    }).subscribe({
      next: migration => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.migrationGenerated') });
        void this.router.navigate(['/dynamic/migrations', migration.id]);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private activateEntity(): void {
    const ent = this.entity();
    if (!ent) {
      return;
    }

    this.api.activateEntity(ent.id).subscribe({
      next: updated => {
        this.entity.set({ ...ent, isActive: updated.isActive });
        this.messages.add({ severity: 'success', summary: this.t('dynamic.entities.activated') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private archiveEntity(): void {
    const ent = this.entity();
    if (!ent) {
      return;
    }

    this.api.archiveEntity(ent.id).subscribe({
      next: updated => {
        this.entity.set({ ...ent, isActive: updated.isActive });
        this.messages.add({ severity: 'success', summary: this.t('dynamic.entities.archived') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private deleteEntity(): void {
    const ent = this.entity();
    if (!ent) {
      return;
    }

    this.api.deleteEntity(ent.id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.entities.delete.success') });
        void this.router.navigate(['/dynamic/entities']);
      },
      error: (err: unknown) => this.messages.add({
        severity: 'error',
        summary: this.resolveDeleteError(err)
      })
    });
  }

  canUpdateEntity(): boolean {
    return this.hasEntityAction('Update');
  }

  canActivateEntity(): boolean {
    return this.hasEntityAction('Activate');
  }

  canArchiveEntity(): boolean {
    return this.hasEntityAction('Archive');
  }

  canDeleteEntity(): boolean {
    return this.hasEntityAction('Delete');
  }

  canGenerateWithAi(): boolean {
    return hasAiGeneratePermission(this.contextService.context());
  }

  openAiAuthoringWizard(): void {
    this.entityAiWizard()?.open();
  }

  private hasEntityAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.entities');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private resolveDeleteError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as Record<string, unknown> | null;
      const code = typeof body?.['error'] === 'string'
        ? body['error']
        : typeof body?.['Error'] === 'string'
          ? body['Error']
          : null;

      if (code === 'dynamic_entity_referenced') {
        return this.t('dynamic.entities.delete.errorReferenced');
      }

      if (code === 'dynamic_entity_not_found' || error.status === 404) {
        return this.t('dynamic.entities.delete.errorNotFound');
      }
    }

    return this.t('dynamic.entities.delete.failed');
  }
}
